/**
 * Layout component for the competitors page.
 * I put it here for now. The new share of voice page will use this layout.
 */

import { Box, Tab, Tabs, Typography } from '@mui/material';
import { typography } from 'components/common/Typography/styles';
import { IconBoldSettings2 } from 'components/icons/components/bold/IconBoldSettings2';
import { useFeatureFlagContext } from 'contexts/FeatureFlag.context';
import { useBrandDetails } from 'features/socialMediaListening';
import { PlotFeature } from 'graphql/generated';
import { useNavigate, useParams } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';

export type SMLCompetitorsLayoutProps = {
  tab: 'competitors' | 'share-of-voice';
  children: React.ReactNode;
};

export const SMLCompetitorsLayout = (props: SMLCompetitorsLayoutProps) => {
  const { tab = 'competitors', children } = props;

  const { brandId = '' } = useParams();
  const navigate = useNavigate();

  const { isFeatureEnabled } = useFeatureFlagContext();

  const { brand } = useBrandDetails({ brandId });

  return (
    <Box
      sx={{
        pt: 16,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={4}
        px={8}
      >
        <Typography
          variant="headline-xl"
          sx={{
            fontSize: 32,
            letterSpacing: '-1.28px',
          }}
        >
          Competitor Analytics
        </Typography>
        {brand && (
          <Box
            borderRadius={10}
            bgcolor={theme.colors?.utility[275]}
            padding={3}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              height: 'center',
            }}
            component="button"
            onClick={() => {
              navigate(
                PlotRoutes.socialListeningSettings({
                  brandId,
                }),
              );
            }}
          >
            <IconBoldSettings2 size={22} color={theme.colors?.utility[600]} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          borderTop: `1px solid ${theme.colors?.utility[300]}`,
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: theme.colors?.utility[200],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          p: theme.spacing(4, 8),
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              switch (v) {
                case 'competitors':
                  navigate(PlotRoutes.socialListeningCompetitors(brandId));
                  break;
                case 'share-of-voice':
                  navigate(
                    PlotRoutes.socialListeningCompetitorsShareOfVoice(brandId),
                  );
                  break;
                default:
                  break;
              }
            }}
            sx={{
              minHeight: 40,

              '.MuiTabs-flexContainer': {
                gap: 2,
              },

              '.MuiButtonBase-root': {
                minHeight: 40,
                py: 2,
                px: 4,
                borderRadius: 50,
                ...typography['headline-md'],
                textTransform: 'none',
                color: `${theme.colors?.utility[700]} !important`,

                '&.Mui-selected': {
                  color: `${theme.colors?.primary.black} !important`,
                  bgcolor: '#23060314',
                },
              },

              '.MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab value="competitors" label="Competitors" />
            {isFeatureEnabled(PlotFeature.CompetitorShareOfVoice) && (
              <Tab value="share-of-voice" label="Share of Voice" />
            )}
          </Tabs>
        </Box>
        <Box id="competitors-layout-tabs-extra-right" />
      </Box>
      {children}
    </Box>
  );
};
